import useSWR from 'swr';
import { fetcherScreening } from '@utils/data-fetching/fetcherScreening';
import type { ApiSchemaScreening } from '@type/api/apiUtilityFnScreening';

type ScreeningItemsResponse = ApiSchemaScreening<'ScreeningItems.ScreeningItemListDto'>;
type ScreeningItemDto = ApiSchemaScreening<'ScreeningItemDto'>;

interface UseSwrScreeningItemsOptions {
  jobId: number | string | null;
  refreshInterval?: number;
  enabled?: boolean;
}

/**
 * Hook to fetch screening items (candidates) for a job
 * Supports polling for real-time status updates
 */
export const useSwrScreeningItems = ({
  jobId,
  refreshInterval = 0,
  enabled = true,
}: UseSwrScreeningItemsOptions) => {
  const shouldFetch = enabled && jobId;

  const { data, error, isLoading, isValidating, mutate } = useSWR<ScreeningItemsResponse>(
    shouldFetch ? `/api/screenings/${jobId}/items` : null,
    async (url: string) => {
      const response = await fetcherScreening<ScreeningItemsResponse>(url);
      if (response.error) {
        throw new Error('Failed to fetch screening items');
      }
      return response.data as ScreeningItemsResponse;
    },
    {
      refreshInterval: shouldFetch ? refreshInterval : 0,
      revalidateOnFocus: false,
    },
  );

  // Calculate processing stats
  const items: ScreeningItemDto[] = (data?.items as ScreeningItemDto[] | undefined) ?? [];
  const isProcessing = items.some((item) => ['PENDING_PARSING', 'PARSING', 'PENDING_MATCHING', 'MATCHING'].includes(item.status ?? ''));
  const completedCount = items.filter((item) => ['MATCHED', 'MATCH_FAILED', 'INTERVIEW_SCHEDULED', 'INTERVIEW_IN_PROGRESS', 'INTERVIEW_COMPLETED', 'INTERVIEW_FAILED'].includes(item.status ?? '')).length;
  const errorCount = items.filter((item) => ['PARSE_FAILED', 'MATCH_FAILED', 'INTERVIEW_FAILED'].includes(item.status ?? '')).length;

  // Get stats from backend
  const backendStats = data?.stats ?? undefined;

  return {
    data,
    items,
    stats: backendStats,
    error,
    isLoading,
    isValidating,
    isProcessing,
    completedCount,
    errorCount,
    totalCount: items.length,
    mutate,
  };
};

export default useSwrScreeningItems;
